import React, { useEffect, useId, useRef, useState } from "react";

import { resolveColorSwatchBackground } from "../../../utils/colorSwatch";
import { normalizeDimensionKey } from "../../../utils/catalogColorCover";

// Presentation primitives for the Product Studio edit workspace. They carry no
// business logic; every class is prefixed `pse-` and styled in
// assets/css/product-studio-edit.css (dark default, light via .white-content).

const PATHS = {
  arrowLeft: "M19 12H5M11 18l-6-6 6-6",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  chevronDown: "M6 9l6 6 6-6",
  chevronLeft: "M15 6l-6 6 6 6",
  chevronRight: "M9 6l6 6-6 6",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35",
  overview: "M4 4h16v16H4zM8 9h8M8 13h8M8 17h5",
  image: "M4 5h16v14H4zM4 16l4.5-4.5 3.5 3.5 3-3L20 17M9 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z",
  variants: "M7 7m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M17 7m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M7 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M17 17m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0",
  cube: "M12 2l9 5v10l-9 5-9-5V7l9-5zM3 7l9 5 9-5M12 12v10",
  details: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8h.01M11 12h1v5h1",
  globe: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.5 2.7 3.8 5.7 3.8 9s-1.3 6.3-3.8 9c-2.5-2.7-3.8-5.7-3.8-9S9.5 5.7 12 3z",
  history: "M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5M12 7v5l3 2",
  tag: "M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8zM7.5 7.5h.01",
  check: "M5 12.5l4.5 4.5L19 7.5",
  checkCircle: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM8 12.5l2.8 2.8L16 10",
  circle: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z",
  alert: "M12 3l10 18H2L12 3zM12 10v4M12 17.5h.01",
  info: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16v-4M12 8h.01",
  star: "M12 3l2.8 5.7 6.2.9-4.5 4.4 1.1 6.2L12 17.3l-5.6 2.9 1.1-6.2L3 9.6l6.2-.9L12 3z",
  trash: "M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3",
  plus: "M12 5v14M5 12h14",
  minus: "M5 12h14",
  upload: "M12 16V4M7 9l5-5 5 5M4 20h16",
  download: "M12 4v12M7 11l5 5 5-5M4 20h16",
  trendUp: "M3 17l6-6 4 4 8-8M15 7h6v6",
  edit: "M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4",
  more: "M5 12h.01M12 12h.01M19 12h.01",
  external: "M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5",
  copy: "M9 9h11v11H9zM5 15H4V4h11v1",
  refresh: "M20 11a8 8 0 0 0-14.9-3.9L3 9M3 4v5h5M4 13a8 8 0 0 0 14.9 3.9L21 15M21 20v-5h-5",
  printer: "M6 9V3h12v6M6 18H4v-7h16v7h-2M8 14h8v7H8z",
  pin: "M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z",
  archive: "M3 4h18v4H3zM5 8v12h14V8M10 12h4",
  meta: "M7 8c-2.2 0-4 2.3-4 5s1.3 4 3 4c2.5 0 4-4 6-7s3.5-5 6-5c1.7 0 3 1.3 3 4s-1.8 5-4 5c-2 0-3.5-2-5-4.5",
  lock: "M6 11h12v10H6zM8.5 11V7.5a3.5 3.5 0 0 1 7 0V11",
  eye: "M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  eyeOff: "M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3 3.8M6.6 6.6C3.8 8.4 2 12 2 12s3.6 7 10 7c1.9 0 3.5-.6 4.9-1.4M9.9 9.9a3 3 0 0 0 4.2 4.2",
  x: "M6 6l12 12M18 6L6 18",
  grip: "M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01",
  layers: "M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5",
  barcode: "M4 5v14M7 5v14M10 5v14M14 5v14M17 5v14M20 5v14",
  clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3 2",
  user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21c0-4 3.6-6 8-6s8 2 8 6",
  arrowUpDown: "M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4",
  sliders: "M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0M14 4v4M8 10v4M16 16v4",
  sparkle: "M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z",
  store: "M4 10v10h16V10M3 4h18l-1 6H4L3 4zM9 20v-6h6v6",
  cart: "M3 4h2l2.4 11h11.2L21 8H6.2M10 20h.01M17 20h.01",
  swap: "M4 8h14l-4-4M20 16H6l4 4",
  undo: "M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3",
  grid: "M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z",
  list: "M9 6h12M9 12h12M9 18h12M4 6h.01M4 12h.01M4 18h.01",
};

export const Icon = ({ name, size = 18, strokeWidth = 1.8, className = "" }) => (
  <svg
    className={`pse-icon ${className}`.trim()}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={name === "more" ? 3 : strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={PATHS[name] || PATHS.circle} />
  </svg>
);

export const cx = (...parts) => parts.filter(Boolean).join(" ");

export const Card = ({ title, subtitle, actions, children, className, bodyClassName, as: Tag = "section", id }) => (
  <Tag className={cx("pse-card", className)} id={id}>
    {title || actions ? (
      <header className="pse-card__head">
        <div className="pse-card__titles">
          {title ? <h3 className="pse-card__title">{title}</h3> : null}
          {subtitle ? <p className="pse-card__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="pse-card__actions">{actions}</div> : null}
      </header>
    ) : null}
    <div className={cx("pse-card__body", bodyClassName)}>{children}</div>
  </Tag>
);

export const Btn = ({
  variant = "secondary",
  size = "md",
  icon,
  iconRight,
  children,
  className,
  block,
  type = "button",
  ...props
}) => (
  <button
    type={type}
    className={cx("pse-btn", `pse-btn--${variant}`, `pse-btn--${size}`, block && "pse-btn--block", className)}
    {...props}
  >
    {icon ? <Icon name={icon} size={size === "sm" ? 15 : 17} /> : null}
    {children ? <span className="pse-btn__label">{children}</span> : null}
    {iconRight ? <Icon name={iconRight} size={size === "sm" ? 14 : 16} /> : null}
  </button>
);

export const Pill = ({ tone = "neutral", icon, children, className, title }) => (
  <span className={cx("pse-pill", `pse-pill--${tone}`, className)} title={title}>
    {icon ? <Icon name={icon} size={13} strokeWidth={2.2} /> : <span className="pse-pill__dot" aria-hidden="true" />}
    <span>{children}</span>
  </span>
);

export const Chip = ({ tone = "neutral", children, className, title }) => (
  <span className={cx("pse-chip", `pse-chip--${tone}`, className)} title={title}>
    {children}
  </span>
);

export const Toggle = ({ checked, onChange, label, description, disabled, id }) => {
  const autoId = useId();
  const inputId = id || `pse-toggle-${autoId}`;
  return (
    <label className={cx("pse-toggle", disabled && "is-disabled")} htmlFor={inputId}>
      <span className="pse-toggle__text">
        <span className="pse-toggle__label">{label}</span>
        {description ? <span className="pse-toggle__description">{description}</span> : null}
      </span>
      <input
        id={inputId}
        type="checkbox"
        role="switch"
        className="pse-toggle__input"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span className="pse-toggle__track" aria-hidden="true">
        <span className="pse-toggle__thumb" />
      </span>
    </label>
  );
};

/** Label + control + hint + optional character counter. */
export const Field = ({ label, hint, counter, error, children, className, htmlFor, optional, optionalLabel }) => (
  <div className={cx("pse-field", error && "has-error", className)}>
    {label ? (
      <label className="pse-field__label" htmlFor={htmlFor}>
        {label}
        {optional ? <span className="pse-field__optional"> ({optionalLabel})</span> : null}
      </label>
    ) : null}
    {children}
    {hint || counter || error ? (
      <div className="pse-field__meta">
        {error ? (
          <span className="pse-field__error" role="alert">
            {error}
          </span>
        ) : (
          <span className="pse-field__hint">{hint}</span>
        )}
        {counter ? <span className="pse-field__counter">{counter}</span> : null}
      </div>
    ) : null}
  </div>
);

export const Segmented = ({ options, value, onChange, ariaLabel, className }) => (
  <div className={cx("pse-segmented", className)} role="radiogroup" aria-label={ariaLabel}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        role="radio"
        aria-checked={value === option.value}
        className={cx("pse-segmented__option", value === option.value && "is-selected")}
        onClick={() => onChange(option.value)}
      >
        {option.icon ? <Icon name={option.icon} size={16} /> : null}
        <span className="pse-segmented__text">
          <span className="pse-segmented__label">{option.label}</span>
          {option.hint ? <span className="pse-segmented__hint">{option.hint}</span> : null}
        </span>
      </button>
    ))}
  </div>
);

export const EmptyState = ({ icon = "info", title, children, action }) => (
  <div className="pse-empty">
    <span className="pse-empty__icon">
      <Icon name={icon} size={26} />
    </span>
    {title ? <strong className="pse-empty__title">{title}</strong> : null}
    {children ? <p className="pse-empty__copy">{children}</p> : null}
    {action ? <div className="pse-empty__action">{action}</div> : null}
  </div>
);

export const Notice = ({ tone = "info", icon, title, children, className, action }) => (
  <div className={cx("pse-notice", `pse-notice--${tone}`, className)} role={tone === "danger" ? "alert" : undefined}>
    <Icon
      name={icon || (tone === "success" ? "checkCircle" : tone === "info" ? "info" : "alert")}
      size={18}
      className="pse-notice__icon"
    />
    <div className="pse-notice__body">
      {title ? <strong className="pse-notice__title">{title}</strong> : null}
      {children ? <div className="pse-notice__copy">{children}</div> : null}
    </div>
    {action ? <div className="pse-notice__action">{action}</div> : null}
  </div>
);

export const Stat = ({ icon, tone = "neutral", label, value, hint, className }) => (
  <div className={cx("pse-stat", `pse-stat--${tone}`, className)}>
    {icon ? (
      <span className="pse-stat__icon">
        <Icon name={icon} size={20} />
      </span>
    ) : null}
    <div className="pse-stat__text">
      <span className="pse-stat__label">{label}</span>
      <strong className="pse-stat__value">{value}</strong>
      {hint ? <span className="pse-stat__hint">{hint}</span> : null}
    </div>
  </div>
);

export const Thumb = ({ src, alt = "", size = 44, className, fallbackLabel }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className={cx("pse-thumb", className)} style={{ width: size, height: size }}>
      {src && !failed ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span className="pse-thumb__fallback" title={fallbackLabel}>
          <Icon name="image" size={Math.max(14, Math.round(size / 2.6))} />
        </span>
      )}
    </span>
  );
};

// Common Albanian colour names, used only when the colour reference has no
// hex code and the name is not a CSS colour (e.g. "Kafe").
const ALBANIAN_COLOR_HEX = {
  zi: "#111111",
  bardhe: "#F7F7F5",
  kuq: "#C62828",
  kuqe: "#C62828",
  blu: "#1E4FC2",
  kalter: "#3B82F6",
  teget: "#1F2A44",
  gjelbert: "#2F6F33",
  jeshil: "#2F6F33",
  verdhe: "#F2C200",
  portokalli: "#EF7D22",
  roze: "#F4A6C0",
  vjollce: "#7C3AED",
  kafe: "#6F4E37",
  taba: "#A0522D",
  bezhe: "#D8C3A5",
  krem: "#F1E6CE",
  gri: "#8A8F98",
  hiri: "#8A8F98",
  argjend: "#C0C4CC",
  ari: "#C9A227",
  bordo: "#7B1E2B",
};

const isCssColorName = (name) => {
  if (!name || typeof document === "undefined") return false;
  const probe = document.createElement("div");
  probe.style.color = String(name).toLowerCase();
  return Boolean(probe.style.color);
};

/**
 * Swatch colour for an option value: the colour reference's hex code, then a
 * CSS/English colour name, then a known Albanian name. Returns null when the
 * colour is genuinely unknown, so we never show an invented hue.
 */
export const resolveSwatch = (value, colorRecords = []) => {
  const key = normalizeDimensionKey(value);
  const record = (colorRecords || []).find((entry) =>
    [entry?.colorName, entry?.name, entry?.albanianName, entry?.englishName].some(
      (name) => name && normalizeDimensionKey(name) === key
    )
  );
  if (record?.hexCode) return resolveColorSwatchBackground(record);
  const cssName = [record?.englishName, value].find(isCssColorName);
  if (cssName) return String(cssName).toLowerCase();
  const albanian = [value, record?.albanianName, record?.colorName]
    .map(normalizeDimensionKey)
    .map((name) => ALBANIAN_COLOR_HEX[name])
    .find(Boolean);
  return albanian || null;
};

export const Swatch = ({ value, colorRecords, size = 16 }) => {
  const background = resolveSwatch(value, colorRecords);
  return (
    <span
      className={cx("pse-swatch", !background && "pse-swatch--unknown")}
      style={{ width: size, height: size, ...(background ? { background } : {}) }}
      aria-hidden="true"
    />
  );
};

export const Stepper = ({ value, onChange, min = 0, max = 9999, ariaLabel, decreaseLabel, increaseLabel }) => {
  const numeric = Number(value || 0);
  const set = (next) => onChange(String(Math.min(max, Math.max(min, next))));
  return (
    <div className="pse-stepper">
      <button
        type="button"
        className="pse-stepper__btn"
        aria-label={decreaseLabel}
        disabled={numeric <= min}
        onClick={() => set(numeric - 1)}
      >
        <Icon name="minus" size={15} strokeWidth={2.2} />
      </button>
      <input
        className="pse-stepper__input"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={ariaLabel}
        value={value}
        placeholder="0"
        onChange={(event) => onChange(event.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
        onFocus={(event) => event.target.select()}
      />
      <button
        type="button"
        className="pse-stepper__btn"
        aria-label={increaseLabel}
        disabled={numeric >= max}
        onClick={() => set(numeric + 1)}
      >
        <Icon name="plus" size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
};

// Counted, so stacked overlays (a drawer plus its confirm dialog) that close in
// the same render cannot restore each other's "hidden" and leave the page
// unscrollable.
let bodyScrollLockCount = 0;
let bodyOverflowBeforeLock = "";

const useBodyScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    if (bodyScrollLockCount === 0) {
      bodyOverflowBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    bodyScrollLockCount += 1;
    return () => {
      bodyScrollLockCount -= 1;
      if (bodyScrollLockCount === 0) document.body.style.overflow = bodyOverflowBeforeLock;
    };
  }, [active]);
};

// Only the most recently opened overlay answers Escape, so closing a confirm
// dialog never also closes the drawer underneath it.
const escapeStack = [];

const useEscape = (active, onEscape) => {
  const handlerRef = useRef(onEscape);
  handlerRef.current = onEscape;
  useEffect(() => {
    if (!active) return undefined;
    const entry = {};
    escapeStack.push(entry);
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && escapeStack[escapeStack.length - 1] === entry) handlerRef.current?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      escapeStack.splice(escapeStack.indexOf(entry), 1);
    };
  }, [active]);
};

// Focuses the element marked `data-autofocus` inside the panel (e.g. a form's
// first field), or the panel itself.
const useInitialFocus = (active, ref) => {
  useEffect(() => {
    if (!active) return undefined;
    const previous = document.activeElement;
    const timer = window.setTimeout(
      () => (ref.current?.querySelector("[data-autofocus]") || ref.current)?.focus(),
      0
    );
    return () => {
      window.clearTimeout(timer);
      if (previous && typeof previous.focus === "function") previous.focus();
    };
  }, [active, ref]);
};

/** Right-hand drawer; becomes a full-height sheet on narrow screens. */
export const Drawer = ({ isOpen, onClose, title, description, icon, footer, children, size = "md", closeLabel }) => {
  const panelRef = useRef(null);
  const titleId = useId();
  useBodyScrollLock(isOpen);
  useEscape(isOpen, onClose);
  useInitialFocus(isOpen, panelRef);
  if (!isOpen) return null;

  return (
    <div className="pse-overlay pse-overlay--drawer">
      <button type="button" className="pse-overlay__backdrop" aria-label={closeLabel} onClick={onClose} />
      <aside
        ref={panelRef}
        tabIndex={-1}
        className={cx("pse-drawer", `pse-drawer--${size}`)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="pse-drawer__head">
          {icon ? (
            <span className="pse-drawer__icon">
              <Icon name={icon} size={22} />
            </span>
          ) : null}
          <div className="pse-drawer__titles">
            <h2 className="pse-drawer__title" id={titleId}>
              {title}
            </h2>
            {description ? <p className="pse-drawer__description">{description}</p> : null}
          </div>
          <button type="button" className="pse-icon-btn" aria-label={closeLabel} onClick={onClose}>
            <Icon name="x" size={20} />
          </button>
        </header>
        <div className="pse-drawer__body">{children}</div>
        {footer ? <footer className="pse-drawer__foot">{footer}</footer> : null}
      </aside>
    </div>
  );
};

export const ConfirmDialog = ({
  isOpen,
  title,
  children,
  confirmLabel,
  cancelLabel,
  tone = "primary",
  onConfirm,
  onCancel,
  isBusy,
}) => {
  const dialogRef = useRef(null);
  const titleId = useId();
  useBodyScrollLock(isOpen);
  useEscape(isOpen && !isBusy, onCancel);
  useInitialFocus(isOpen, dialogRef);
  if (!isOpen) return null;

  return (
    <div className="pse-overlay pse-overlay--dialog">
      <button
        type="button"
        className="pse-overlay__backdrop"
        aria-label={cancelLabel}
        onClick={isBusy ? undefined : onCancel}
      />
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={cx("pse-dialog", `pse-dialog--${tone}`)}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <span className="pse-dialog__icon">
          <Icon name={tone === "danger" ? "alert" : "info"} size={24} />
        </span>
        <h2 className="pse-dialog__title" id={titleId}>
          {title}
        </h2>
        <div className="pse-dialog__copy">{children}</div>
        <div className="pse-dialog__actions">
          <Btn variant="ghost" onClick={onCancel} disabled={isBusy}>
            {cancelLabel}
          </Btn>
          <Btn variant={tone === "danger" ? "danger" : "primary"} onClick={onConfirm} disabled={isBusy}>
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
};

/** Small popover menu (⋯). Items: { label, icon, onClick, tone, disabled, title }. */
export const Menu = ({ label, items, icon = "more", buttonLabel, align = "end", variant = "icon" }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div className="pse-menu" ref={rootRef}>
      {variant === "icon" ? (
        <button
          type="button"
          className="pse-icon-btn"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={label}
          title={label}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((current) => !current);
          }}
        >
          <Icon name={icon} size={18} />
        </button>
      ) : (
        <Btn
          variant="secondary"
          icon={icon}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(event) => {
            event.stopPropagation();
            setOpen((current) => !current);
          }}
        >
          {buttonLabel}
        </Btn>
      )}
      {open ? (
        <div className={cx("pse-menu__list", `pse-menu__list--${align}`)} role="menu">
          {items
            .filter(Boolean)
            .map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                className={cx("pse-menu__item", item.tone && `pse-menu__item--${item.tone}`)}
                disabled={item.disabled}
                title={item.title}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  item.onClick?.();
                }}
              >
                {item.icon ? <Icon name={item.icon} size={16} /> : null}
                <span>{item.label}</span>
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
};

export const Spinner = ({ label }) => (
  <span className="pse-spinner" role="status" aria-live="polite">
    <span className="pse-spinner__ring" aria-hidden="true" />
    {label ? <span className="pse-spinner__label">{label}</span> : null}
  </span>
);

/** Controlled text input/textarea with a character counter. */
export const TextInput = ({ multiline, rows = 3, maxLength, value, onChange, className, ...props }) => {
  const Tag = multiline ? "textarea" : "input";
  return (
    <Tag
      className={cx("pse-input", multiline && "pse-input--multiline", className)}
      rows={multiline ? rows : undefined}
      maxLength={maxLength}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      {...props}
    />
  );
};

export const Select = ({ value, onChange, children, className, ...props }) => (
  <span className={cx("pse-select", className)}>
    <select value={value ?? ""} onChange={(event) => onChange(event.target.value)} {...props}>
      {children}
    </select>
    <Icon name="chevronDown" size={16} className="pse-select__chevron" />
  </span>
);

export const MoneyInput = ({ value, onChange, id, placeholder = "0.00", ...props }) => (
  <span className="pse-money">
    <span className="pse-money__currency" aria-hidden="true">
      €
    </span>
    <input
      id={id}
      className="pse-input pse-money__input"
      type="text"
      inputMode="decimal"
      value={value ?? ""}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value.replace(",", ".").replace(/[^0-9.]/g, ""))}
      {...props}
    />
  </span>
);
